import { useState, type ChangeEvent } from 'react';
import './Input.css';

interface InputProps {
  id: string;
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onBlur?: () => void;
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'textarea' | 'select';
  placeholder?: string;
  required?: boolean;
  rows?: number;
  error?: string;
  isValid?: boolean;
  isTouched?: boolean;
  floating?: boolean;
  className?: string;
  options?: { value: string; label: string }[];
  autoComplete?: string;
}

export function Input({
  id,
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder = '',
  required = false,
  rows = 4,
  error,
  isValid = false,
  isTouched = false,
  floating = false,
  className = '',
  options = [],
  autoComplete,
}: InputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const isTextArea = type === 'textarea';
  const isSelect = type === 'select';
  const isPassword = type === 'password';

  const hasError = Boolean(error && isTouched);
  const showSuccess = Boolean(isValid && isTouched && !error);

  const inputType = isPassword && showPassword ? 'text' : type;

  const renderInput = () => {
    if (isTextArea) {
      return (
        <>
          <textarea
            id={id}
            className={`input-control ${hasError ? 'control-error' : ''} ${showSuccess ? 'control-success' : ''}`}
            placeholder={floating ? ' ' : placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            rows={rows}
          />
          {floating && (
            <label htmlFor={id} className={`label-floating textarea-label-floating`}>
              {label}
            </label>
          )}
        </>
      );
    }

    if (isSelect) {
      return (
        <>
          <select
            id={id}
            className={`input-control select-control ${hasError ? 'control-error' : ''} ${showSuccess ? 'control-success' : ''}`}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {floating && (
            <label htmlFor={id} className="label-floating">
              {label}
            </label>
          )}
        </>
      );
    }

    return (
      <div className="input-wrapper">
        <input
          id={id}
          type={inputType}
          className={`input-control ${hasError ? 'control-error' : ''} ${showSuccess ? 'control-success' : ''} ${isPassword ? 'input-password' : ''}`}
          placeholder={floating ? ' ' : placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          autoComplete={autoComplete}
        />

        {floating && (
          <label htmlFor={id} className="label-floating">
            {label}
          </label>
        )}

        {isPassword && value && (
          <button 
            type="button" 
            className="input-password-toggle" 
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        )}

        {showSuccess && !isPassword && (
          <span className="input-feedback-success" aria-hidden="true">&#10003;</span>
        )}
      </div>
    );
  };

  if (floating) {
    return (
      <div className={`input-group-floating ${className}`}>
        {renderInput()}
        {hasError && <span className="input-group-error">{error}</span>}
      </div>
    );
  }

  return (
    <div className={`input-group-standard ${className}`}>
      <label htmlFor={id} className="label-standard">
        {label}
      </label>
      {renderInput()}
      {hasError && <span className="input-group-error">{error}</span>}
    </div>
  );
}
